import { useState } from "react";
import { Link } from "react-router-dom";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { ArrowRight, Compass, Users, Clock, BookOpenText, ChevronDown, ChevronUp } from "lucide-react";
import { NAVIGATOR, CONTACTS, PLANNED, ARTICLES, EC_DISCLAIMER } from "@/lib/employeeCenter";

function SectionTitle({ icon: Icon, title, sub, testid }) {
  return (
    <div className="mb-4" data-testid={testid}>
      <h2 className="font-display text-lg font-bold text-[#0A2A5E] flex items-center gap-2"><Icon className="w-5 h-5 text-[#F5A623]" aria-hidden="true" />{title}</h2>
      {sub && <p className="text-sm text-slate-600 mt-1">{sub}</p>}
    </div>
  );
}

// Client-side chooser that only routes to existing authorized pages
export function SupportNavigator() {
  const [pick, setPick] = useState(null);
  const chosen = NAVIGATOR.find((n) => n.id === pick);
  return (
    <Card className="p-5 sm:p-6 bg-white border-slate-200" data-testid="support-navigator">
      <SectionTitle icon={Compass} title="Employee Support Navigator" sub="What do you need help with? Choose one — we'll point you to the right place in LUMIS HR." />
      <div className="grid sm:grid-cols-2 gap-2" role="radiogroup" aria-label="What do you need help with?">
        {NAVIGATOR.map((n) => (
          <button key={n.id} type="button" role="radio" aria-checked={pick === n.id} data-testid={`navigator-option-${n.id}`} onClick={() => setPick(n.id)}
            className={`text-left rounded-lg border px-4 py-3 text-sm font-medium transition-colors ${pick === n.id ? "border-[#F5A623] bg-[#FDECD0] text-[#7C3F00]" : "border-slate-200 text-slate-700 hover:bg-slate-50"}`}>
            {n.label}
          </button>
        ))}
      </div>
      {chosen && (
        <div className="mt-4 rounded-lg bg-[#F5F9FE] border border-slate-200 p-4 flex flex-col sm:flex-row sm:items-center gap-3" data-testid="navigator-result">
          <p className="text-sm text-slate-700 flex-1">{chosen.detail}</p>
          <Link to={chosen.to}><Button data-testid={`navigator-go-${chosen.id}`} className="bg-[#0A2A5E] hover:bg-[#0F3F8A] text-white">{chosen.cta} <ArrowRight className="w-4 h-4 ml-2" aria-hidden="true" /></Button></Link>
        </div>
      )}
    </Card>
  );
}

export function ContactGuide() {
  return (
    <Card className="p-5 sm:p-6 bg-white border-slate-200" data-testid="contact-guide">
      <SectionTitle icon={Users} title="Who Should I Contact?" sub="A general guide. Your organization's policy decides the exact process." />
      <dl className="divide-y divide-slate-100">
        {CONTACTS.map((c) => (
          <div key={c.who} className="py-3 grid sm:grid-cols-3 gap-1 sm:gap-4 text-sm">
            <dt className="font-semibold text-[#0A2A5E]">{c.who}</dt>
            <dd className="sm:col-span-2 text-slate-600">{c.when}</dd>
          </div>
        ))}
      </dl>
    </Card>
  );
}

// Inactive placeholders — no navigation, actions, API or data collection
export function PlannedCards() {
  return (
    <section data-testid="planned-section" aria-labelledby="planned-title">
      <SectionTitle icon={Clock} title="Planned Employee Support" sub="These workflows are planned. They are not active in this release." />
      <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {PLANNED.map((p) => (
          <Card key={p.title} aria-disabled="true" data-testid={`planned-card-${p.title.toLowerCase().replace(/[^a-z]+/g, "-")}`} className="p-4 bg-slate-50 border-dashed border-slate-300 opacity-80 select-none">
            <span className="inline-block text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-full bg-slate-200 text-slate-600">Planned</span>
            <h3 className="font-display font-bold text-slate-600 mt-2">{p.title}</h3>
            <p className="text-xs text-slate-500 mt-1">{p.note}</p>
          </Card>
        ))}
      </div>
    </section>
  );
}

function Article({ a }) {
  const [open, setOpen] = useState(false);
  return (
    <li className="border border-slate-200 rounded-lg bg-white" data-testid={`article-${a.id}`}>
      <button type="button" aria-expanded={open} aria-controls={`article-body-${a.id}`} data-testid={`article-toggle-${a.id}`} onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left">
        <span><span className="font-semibold text-[#0A2A5E] text-sm">{a.title}</span><span className="block text-[11px] text-slate-500 mt-0.5">{a.minutes} min read · LUMIS HR Solutions LLC</span></span>
        {open ? <ChevronUp className="w-4 h-4 text-slate-500" aria-hidden="true" /> : <ChevronDown className="w-4 h-4 text-slate-500" aria-hidden="true" />}
      </button>
      {open && (
        <div id={`article-body-${a.id}`} data-testid={`article-body-${a.id}`} className="px-4 pb-4 text-sm text-slate-700 space-y-2">
          {a.body.map((p, i) => <p key={i}>{p}</p>)}
          <p className="text-[11px] text-slate-500 pt-2 border-t border-slate-100" data-testid={`article-disclaimer-${a.id}`}>{EC_DISCLAIMER}</p>
        </div>
      )}
    </li>
  );
}

export function Articles() {
  return (
    <section data-testid="articles-section">
      <SectionTitle icon={BookOpenText} title="Workplace articles" sub="Short, general reading. Expand an article to read it here." />
      <ul className="space-y-2">{ARTICLES.map((a) => <Article key={a.id} a={a} />)}</ul>
    </section>
  );
}
